import React from 'react';

import './wellness-card.scss';

interface WellnessCardProps {
  item: {
    id: string;
    title: string;
    subtitle?: string;
    type: 'Article' | 'Video' | 'External';
    readTime: string;
    image?: string;
    icon?: string;
    category?: string;
  };
  variant?: 'featured' | 'latest' | 'video';
  onClick?: () => void;
  onHover?: (isHovered: boolean) => void;
}

const WellnessCard: React.FC<WellnessCardProps> = ({ 
  item, 
  variant = 'latest', 
  onClick, 
  onHover 
}) => {
  const styles = {
    card: {
      backgroundColor: 'white',
      borderRadius: '12px',
      overflow: 'hidden',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      cursor: 'pointer',
      transition: 'transform 0.2s ease'
    },
    cardHover: {
      transform: 'translateY(-2px)'
    },
    featuredCard: {
      backgroundColor: 'white',
      borderRadius: '12px',
      overflow: 'hidden',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
      cursor: 'pointer'
    },
    image: {
      width: '100%',
      height: variant === 'featured' ? '140px' : '100px',
      background: variant === 'featured' 
        ? 'linear-gradient(45deg, #667eea, #764ba2)'
        : '#e0e0e0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: variant === 'featured' ? 'white' : '#999',
      fontSize: variant === 'featured' ? '32px' : '24px'
    },
    content: {
      padding: variant === 'featured' ? '16px' : '12px'
    },
    title: {
      fontSize: variant === 'featured' ? '16px' : '14px',
      fontWeight: 'bold',
      color: '#333',
      marginBottom: variant === 'featured' ? '8px' : '6px',
      lineHeight: '1.3'
    },
    subtitle: {
      fontSize: variant === 'featured' ? '13px' : '12px',
      color: '#666',
      lineHeight: '1.4',
      marginBottom: '8px'
    },
    meta: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    badge: {
      fontSize: '11px',
      fontWeight: '500',
      padding: '2px 8px',
      borderRadius: '10px',
      backgroundColor: '#e8f5e8',
      color: '#2e7d32'
    },
    videoBadge: {
      backgroundColor: '#fff3e0',
      color: '#f57c00'
    },
    linkBadge: {
      backgroundColor: '#e3f2fd',
      color: '#1976d2'
    },
    readTime: {
      fontSize: '12px',
      color: '#666'
    },
    icon: {
      width: variant === 'featured' ? '40px' : '32px',
      height: variant === 'featured' ? '40px' : '32px',
      color: variant === 'featured' ? 'white' : '#999'
    }
  };

  const getBadgeStyle = () => {
    switch (item.type) {
      case 'Video':
        return { ...styles.badge, ...styles.videoBadge };
      case 'External':
        return { ...styles.badge, ...styles.linkBadge };
      default:
        return styles.badge;
    }
  };

  const getImageContent = () => {
    if (item.image) {
      return item.image;
    }
    if (item.icon) {
      return (
        <svg style={styles.icon} viewBox="0 0 24 24" fill="currentColor">
          <path d={item.icon} />
        </svg>
      );
    }
    return item.type === 'Video' ? '▶️' : '📖';
  };

  return (
    <div
      style={styles.card}
      onClick={onClick}
      onMouseEnter={() => onHover?.(true)}
      onMouseLeave={() => onHover?.(false)}
    >
      <div style={styles.image}>
        {getImageContent()}
      </div>
      <div style={styles.content}>
        <div style={styles.title}>{item.title}</div>
        {item.subtitle && <div style={styles.subtitle}>{item.subtitle}</div>}
        <div style={styles.meta}>
          <span style={getBadgeStyle()}>{item.type}</span>
          <span style={styles.readTime}>{item.readTime}</span>
        </div>
      </div>
    </div>
  );
};

export default WellnessCard;
